import type { ForqSupabaseClient } from './client';
import {
  normalizeMachineSearchInput,
  normalizeMachineTypeInput,
  type AddMachineAttachmentInput,
  type CreateMachineInput,
  type MachineAttachmentRecord,
  type MachineAttributeValue,
  type MachineCustomerOption,
  type MachineDetailRecord,
  type MachineTaxScheme,
  type MachineListRecord,
  type MachinePage,
  type MachineSearchInput,
  type MachinesGateway,
  type MachineStatus,
  type MachineTypeAdminRecord,
  type MachineTypeRecord,
  type SaveMachineTypeInput,
  type UpdateMachineInput,
} from '@forq/machines-data';
import { SupabaseOperationError } from './errors';

interface SearchRow {
  readonly id: string;
  readonly machine_number: string;
  readonly type_id: string;
  readonly type_name: string;
  readonly manufacturer: string;
  readonly model: string;
  readonly serial_number: string;
  readonly construction_year: number | null;
  readonly status: MachineStatus;
  readonly customer_id: string | null;
  readonly customer_number: string;
  readonly customer_name: string;
  readonly location: string;
  readonly image_url: string;
  readonly purchase_price: number | string;
  readonly sales_price: number | string;
  readonly rental_daily_rate: number | string;
  readonly rental_weekly_rate: number | string;
  readonly rental_monthly_rate: number | string;
  readonly rental_minimum_days: number | string;
  readonly active_since: string;
  readonly active_until: string | null;
  readonly updated_at: string;
  readonly total_count: number | string;
  readonly attributes: Record<string, MachineAttributeValue> | null;
}

function messageOf(error: { readonly message: string } | null): string { return error?.message ?? 'Unknown Supabase error.'; }
function detail(value: unknown): MachineDetailRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SupabaseOperationError('machines.detail', 'The machine detail has an invalid shape.');
  }
  return value as MachineDetailRecord;
}
function arrayOf<T>(value: unknown, operation: string): readonly T[] {
  if (!Array.isArray(value)) {
    throw new SupabaseOperationError(operation, 'The database returned an invalid list.');
  }
  return value as readonly T[];
}
function attachment(value: unknown): MachineAttachmentRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new SupabaseOperationError('machines.attachment', 'The attachment has an invalid shape.');
  }
  return value as MachineAttachmentRecord;
}
function payload(input: CreateMachineInput | UpdateMachineInput): Readonly<Record<string, unknown>> {
  return {
    typeId: input.typeId,
    customerId: input.customerId ?? null,
    manufacturer: input.manufacturer,
    model: input.model,
    serialNumber: input.serialNumber ?? '',
    constructionYear: input.constructionYear ?? null,
    status: input.status ?? 'customer',
    location: input.location ?? '',
    imageUrl: input.imageUrl ?? '',
    purchasePrice: input.purchasePrice ?? 0,
    salesPrice: input.salesPrice ?? 0,
    rentalDailyRate: input.rentalDailyRate ?? 0,
    rentalWeeklyRate: input.rentalWeeklyRate ?? 0,
    rentalMonthlyRate: input.rentalMonthlyRate ?? 0,
    rentalMinimumDays: input.rentalMinimumDays ?? 1,
    activeSince: input.activeSince ?? '',
    notes: input.notes ?? '',
    warengruppeId: input.warengruppeId ?? null,
    attributes: input.attributes ?? {},
  };
}

export class SupabaseMachinesGateway implements MachinesGateway {
  constructor(private readonly client: ForqSupabaseClient) {}

  async listMachineTypes(tenantId: string): Promise<readonly MachineTypeRecord[]> {
    const { data, error } = await this.client.rpc('machine_type_catalog', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('machines.types', messageOf(error));
    return arrayOf<MachineTypeRecord>(data ?? [], 'machines.types');
  }

  async listMachineTypesForAdmin(tenantId: string): Promise<readonly MachineTypeAdminRecord[]> {
    const { data, error } = await this.client.rpc('machine_type_admin_catalog', { p_tenant_id: tenantId });
    if (error) throw new SupabaseOperationError('machines.types.admin', messageOf(error));
    return arrayOf<MachineTypeAdminRecord>(data ?? [], 'machines.types.admin');
  }

  async saveMachineType(input: SaveMachineTypeInput): Promise<readonly MachineTypeAdminRecord[]> {
    const normalized = normalizeMachineTypeInput(input);
    const { tenantId, ...machineType } = normalized;
    const { data, error } = await this.client.rpc('machine_type_save', {
      p_tenant_id: tenantId,
      p_payload: machineType,
    });
    if (error) throw new SupabaseOperationError('machines.types.save', messageOf(error));
    return arrayOf<MachineTypeAdminRecord>(data ?? [], 'machines.types.save');
  }

  async searchCustomerOptions(tenantId: string, query = ''): Promise<readonly MachineCustomerOption[]> {
    const { data, error } = await this.client.rpc('machine_customer_options', {
      p_tenant_id: tenantId,
      p_query: query.trim(),
      p_limit: 50,
    });
    if (error) throw new SupabaseOperationError('machines.customers', messageOf(error));
    return arrayOf<MachineCustomerOption>(data ?? [], 'machines.customers');
  }

  async searchMachines(input: MachineSearchInput): Promise<MachinePage> {
    const normalized = normalizeMachineSearchInput(input);
    const { data, error } = await this.client.rpc('machine_search', {
      p_tenant_id: normalized.tenantId,
      p_query: normalized.query,
      p_status: normalized.status === 'all' ? null : normalized.status,
      p_type_id: normalized.typeId === 'all' ? null : normalized.typeId,
      p_customer_number: normalized.customerNumber || null,
      p_bestand_from: normalized.bestandFrom || null,
      p_bestand_to: normalized.bestandTo || null,
      p_limit: normalized.pageSize,
      p_offset: (normalized.page - 1) * normalized.pageSize,
    });
    if (error) throw new SupabaseOperationError('machines.search', messageOf(error));
    const rows = (data ?? []) as SearchRow[];
    const items: readonly MachineListRecord[] = rows.map((row) => ({
      id: row.id,
      number: row.machine_number,
      typeId: row.type_id,
      typeName: row.type_name,
      manufacturer: row.manufacturer ?? '',
      model: row.model ?? '',
      serialNumber: row.serial_number ?? '',
      constructionYear: row.construction_year,
      status: row.status,
      customerId: row.customer_id,
      customerNumber: row.customer_number ?? '',
      customerName: row.customer_name ?? '',
      location: row.location ?? '',
      imageUrl: row.image_url ?? '',
      attributes: row.attributes ?? {},
      purchasePrice: Number(row.purchase_price ?? 0),
      salesPrice: Number(row.sales_price ?? 0),
      rentalDailyRate: Number(row.rental_daily_rate ?? 0),
      rentalWeeklyRate: Number(row.rental_weekly_rate ?? 0),
      rentalMonthlyRate: Number(row.rental_monthly_rate ?? 0),
      rentalMinimumDays: Number(row.rental_minimum_days ?? 1),
      activeSince: row.active_since ?? '',
      activeUntil: row.active_until ?? null,
      updatedAt: row.updated_at,
    }));
    return { items, total: Number(rows[0]?.total_count ?? 0), page: normalized.page, pageSize: normalized.pageSize };
  }

  async getMachine(tenantId: string, machineId: string): Promise<MachineDetailRecord> {
    const { data, error } = await this.client.rpc('machine_get', { p_tenant_id: tenantId, p_machine_id: machineId });
    if (error) throw new SupabaseOperationError('machines.get', messageOf(error));
    return detail(data);
  }

  async createMachine(input: CreateMachineInput): Promise<MachineDetailRecord> {
    const { data, error } = await this.client.rpc('machine_create', { p_tenant_id: input.tenantId, p_payload: payload(input) });
    if (error) throw new SupabaseOperationError('machines.create', messageOf(error));
    return detail(data);
  }

  async updateMachine(input: UpdateMachineInput): Promise<MachineDetailRecord> {
    const { data, error } = await this.client.rpc('machine_update', {
      p_tenant_id: input.tenantId,
      p_machine_id: input.machineId,
      p_payload: payload(input),
    });
    if (error) throw new SupabaseOperationError('machines.update', messageOf(error));
    return detail(data);
  }

  async listAttachments(tenantId: string, machineId: string): Promise<readonly MachineAttachmentRecord[]> {
    const { data, error } = await this.client.rpc('machine_attachment_list', { p_tenant_id: tenantId, p_machine_id: machineId });
    if (error) throw new SupabaseOperationError('machines.attachments', messageOf(error));
    return arrayOf<MachineAttachmentRecord>(data ?? [], 'machines.attachments');
  }

  async getAttachment(tenantId: string, attachmentId: string): Promise<MachineAttachmentRecord> {
    const { data, error } = await this.client.rpc('machine_attachment_get', { p_tenant_id: tenantId, p_attachment_id: attachmentId });
    if (error) throw new SupabaseOperationError('machines.attachment.get', messageOf(error));
    return attachment(data);
  }

  async addAttachment(input: AddMachineAttachmentInput): Promise<MachineAttachmentRecord> {
    const { tenantId, ...rest } = input;
    const { data, error } = await this.client.rpc('machine_attachment_add', { p_tenant_id: tenantId, p_payload: rest });
    if (error) throw new SupabaseOperationError('machines.attachment.add', messageOf(error));
    return attachment(data);
  }

  async removeAttachment(tenantId: string, attachmentId: string): Promise<void> {
    const { error } = await this.client.rpc('machine_attachment_remove', { p_tenant_id: tenantId, p_attachment_id: attachmentId });
    if (error) throw new SupabaseOperationError('machines.attachment.remove', messageOf(error));
  }

  async updateMachineNotes(tenantId: string, machineId: string, notes: string): Promise<MachineDetailRecord> {
    const { data, error } = await this.client.rpc('machine_notes_update', { p_tenant_id: tenantId, p_machine_id: machineId, p_notes: notes });
    if (error) throw new SupabaseOperationError('machines.notes', messageOf(error));
    return detail(data);
  }

  async setMachineTaxScheme(tenantId: string, machineId: string, scheme: MachineTaxScheme): Promise<MachineDetailRecord> {
    const { data, error } = await this.client.rpc('machine_set_tax_scheme', {
      p_tenant_id: tenantId, p_machine_id: machineId, p_tax_scheme: scheme,
    });
    if (error) throw new SupabaseOperationError('machines.taxScheme', messageOf(error));
    return detail(data);
  }

  async setMachineInspection(
    tenantId: string,
    machineId: string,
    nextUvvDate: string | null,
    lastUvvDate: string | null,
  ): Promise<MachineDetailRecord> {
    const { data, error } = await this.client.rpc('machine_set_inspection', {
      p_tenant_id: tenantId,
      p_machine_id: machineId,
      p_next_uvv_date: nextUvvDate,
      p_last_uvv_date: lastUvvDate,
    });
    if (error) throw new SupabaseOperationError('machines.inspection', messageOf(error));
    return detail(data);
  }
}
